import { Module, MutationTree } from 'vuex'

// 键位事件消息内容（用于钢琴键按下/抬起等实时事件）
export interface KeyPositionContent {
  // 事件动作：note_on / note_off
  action: string
  // 左手或右手
  hand: string
  // 键位名称（如 C4）
  key_name: string
  // MIDI 音符编号
  midi_id: number
  // 事件发生时间戳（毫秒）
  timestamp: number
}

// 统一消息结构（兼容 assistant / 演奏日志 / 键位消息）
export interface PianoPerformLogMessage {
  // 消息类型（如 song_prompt / planning-group / end）
  type: string
  // 消息唯一 ID
  id: string
  // 会话 ID（用于多会话归类）
  sessionId: string
  // ISO 时间字符串
  timestamp: string
  // 消息内容（文本/键位结构化数据/其他对象）
  content: string | KeyPositionContent | Record<string, unknown>
  // 消息状态码（业务约定）
  status: number
}

// 学习模式数据（来源于 assistant store）
export interface LearningData {
  // 选择的文件路径列表
  filePaths: string[]
  // 当前学习模式
  mode: string
}

// 从创建任务页跳转到演奏页时透传的歌曲/舞蹈名
export interface PendingPerformancePayload {
  // 需要自动触发演奏的歌曲名
  songName: string
  // 需要同步展示的视频舞蹈名（可选）
  danceName?: string
}

// 按会话分组保存普通消息
export interface AssistantSessionMessages {
  // 会话 ID
  sessionId: string
  // 会话下的消息列表
  messages: PianoPerformLogMessage[]
}

// 大模型消息角色类型
export type GenerativeMessageRole = 'user' | 'assistant' | 'system'

// 大模型会话中的单条消息
export interface GenerativeMessage {
  // 消息唯一 ID
  id: string
  // 消息角色
  role: GenerativeMessageRole
  // 文本内容
  content: string
  // 时间戳（毫秒）
  timestamp: number
  // 是否处于流式输出中
  isStreaming?: boolean
  // 错误信息（可选）
  error?: string
}

// 单个 Generative 会话状态
export interface GenerativeSessionState {
  // 已渲染消息列表
  messages: GenerativeMessage[]
  // 加载状态
  isLoading: boolean
  // 错误文案
  error: string | null
  // 线程 ID（后端会话线程）
  threadId?: string
  // 后端原始线程消息
  threadMessages?: any[]
  // 后端线程中断信息
  threadInterrupt?: any
}

// SET_GENERATIVE_MESSAGES 载荷
export interface SetGenerativeMessagesPayload {
  // 要覆盖到会话中的消息数组
  messages: GenerativeMessage[]
  // 指定会话（不传则走当前会话）
  sessionId?: string | null
}

// SET_GENERATIVE_LOADING 载荷
export interface SetGenerativeLoadingPayload {
  // 目标 loading 值
  loading: boolean
  // 指定会话（不传则走当前会话）
  sessionId?: string | null
}

// SET_GENERATIVE_ERROR 载荷
export interface SetGenerativeErrorPayload {
  // 错误信息（null 表示清空）
  error: string | null
  // 指定会话（不传则走当前会话）
  sessionId?: string | null
}

// SET_THREAD_INFO 载荷
export interface SetThreadInfoPayload {
  // 线程信息（与 React 端字段保持一致）
  threadInfo: { threadId?: string, messages?: any[], interrupt?: any }
  // 指定会话（不传则走当前会话）
  sessionId?: string | null
}

// robot 模块总状态（融合 assistant + generativeUI 两套 store）
export interface RobotState {
  // assistant 普通消息（按 session 分组）
  messages: AssistantSessionMessages[]
  // 演奏日志消息（兼容现有 Vue 页面）
  performLogMessages: PianoPerformLogMessage[]
  // 键位事件消息
  keyPositionMessages: PianoPerformLogMessage[]
  // 学习数据（文件路径 + 模式）
  learningData: LearningData
  // assistant 当前会话 ID
  currentSessionId: string | null
  // 待触发演奏上下文（用于路由跳转跨页面传递）
  pendingPerformance: PendingPerformancePayload | null
  // generativeUI 的多会话状态表
  sessionStates: Record<string, GenerativeSessionState>
  // generativeUI 当前会话 ID
  generativeCurrentSessionId: string | null
}

// 生成 generative 会话初始状态
const getInitialGenerativeSessionState = (): GenerativeSessionState => ({
  messages: [],
  isLoading: false,
  error: null,
  threadId: undefined
})

/** 与模块注册时一致；供退出登录等场景 `replaceState` 使用 */
export function getRobotInitialState (): RobotState {
  return {
    messages: [],
    performLogMessages: [],
    keyPositionMessages: [],
    learningData: { filePaths: [], mode: '' },
    currentSessionId: 'session-1',
    pendingPerformance: null,
    sessionStates: {},
    generativeCurrentSessionId: null
  }
}

// robot 模块初始状态
const state = (): RobotState => getRobotInitialState()

const mutations: MutationTree<RobotState> = {
  // ===== assistant store =====
  // 添加普通消息：按 sessionId 聚合到对应会话
  ADD_MESSAGES (state, msg: PianoPerformLogMessage) {
    const sessionIndex = state.messages.findIndex(
      (session) => session.sessionId === msg.sessionId
    )

    if (sessionIndex !== -1) {
      const updatedMessages = [...state.messages]
      updatedMessages[sessionIndex] = {
        ...updatedMessages[sessionIndex],
        messages: [...updatedMessages[sessionIndex].messages, msg]
      }
      state.messages = updatedMessages
      return
    }

    state.messages = [
      ...state.messages,
      {
        sessionId: msg.sessionId,
        messages: [msg]
      }
    ]
  },
  // 追加一条演奏日志消息
  ADD_PERFORM_LOG_MESSAGE (state, msg: PianoPerformLogMessage) {
    state.performLogMessages = [...state.performLogMessages, msg]
  },
  // 清空演奏日志消息
  CLEAR_PERFORM_LOG_MESSAGES (state) {
    state.performLogMessages = []
  },
  // 设置学习数据（文件路径 + 模式）
  SET_LEARNING_DATA (state, data: LearningData) {
    state.learningData = data
  },
  // 设置 assistant 当前会话 ID
  SET_CURRENT_SESSION_ID (state, sessionId: string | null) {
    state.currentSessionId = sessionId
  },
  // 写入待触发演奏上下文
  SET_PENDING_PERFORMANCE (state, payload: PendingPerformancePayload) {
    state.pendingPerformance = payload
  },
  // 清空待触发演奏上下文
  CLEAR_PENDING_PERFORMANCE (state) {
    state.pendingPerformance = null
  },
  // 追加一条键位事件消息
  ADD_KEY_POSITION_MESSAGE (state, msg: PianoPerformLogMessage) {
    state.keyPositionMessages = [...state.keyPositionMessages, msg]
  },
  // 清空键位事件消息
  CLEAR_KEY_POSITION_MESSAGES (state) {
    state.keyPositionMessages = []
  },
  // 清空普通消息会话列表
  CLEAR_MESSAGES (state) {
    state.messages = []
  },

  // ===== generative UI store =====
  // 设置 generative 当前会话 ID
  SET_GENERATIVE_CURRENT_SESSION_ID (state, sessionId: string | null) {
    state.generativeCurrentSessionId = sessionId
  },
  // 创建新的 generative 会话并切换为当前会话
  CREATE_GENERATIVE_SESSION_STATE (state, sessionId: string) {
    state.sessionStates = {
      ...state.sessionStates,
      [sessionId]: getInitialGenerativeSessionState()
    }
    state.generativeCurrentSessionId = sessionId
  },
  // 覆盖指定会话消息列表（不传 sessionId 使用当前会话）
  SET_GENERATIVE_MESSAGES (state, payload: SetGenerativeMessagesPayload) {
    const targetSessionId = payload.sessionId ?? state.generativeCurrentSessionId
    if (!targetSessionId || !state.sessionStates[targetSessionId]) return

    state.sessionStates = {
      ...state.sessionStates,
      [targetSessionId]: {
        ...state.sessionStates[targetSessionId],
        messages: payload.messages
      }
    }
  },
  // 追加一条 generative 消息（按 id 去重）
  ADD_GENERATIVE_MESSAGE (
    state,
    payload: { message: GenerativeMessage, sessionId?: string | null }
  ) {
    const targetSessionId = payload.sessionId ?? state.generativeCurrentSessionId
    if (!targetSessionId || !state.sessionStates[targetSessionId]) return

    const currentState = state.sessionStates[targetSessionId]
    const existingMessage = currentState.messages.find((msg) => msg.id === payload.message.id)
    if (existingMessage) return

    state.sessionStates = {
      ...state.sessionStates,
      [targetSessionId]: {
        ...currentState,
        messages: [...currentState.messages, payload.message]
      }
    }
  },
  // 设置指定会话 loading 状态
  SET_GENERATIVE_LOADING (state, payload: SetGenerativeLoadingPayload) {
    const targetSessionId = payload.sessionId ?? state.generativeCurrentSessionId
    if (!targetSessionId || !state.sessionStates[targetSessionId]) return

    state.sessionStates = {
      ...state.sessionStates,
      [targetSessionId]: {
        ...state.sessionStates[targetSessionId],
        isLoading: payload.loading
      }
    }
  },
  // 设置指定会话错误信息
  SET_GENERATIVE_ERROR (state, payload: SetGenerativeErrorPayload) {
    const targetSessionId = payload.sessionId ?? state.generativeCurrentSessionId
    if (!targetSessionId || !state.sessionStates[targetSessionId]) return

    state.sessionStates = {
      ...state.sessionStates,
      [targetSessionId]: {
        ...state.sessionStates[targetSessionId],
        error: payload.error
      }
    }
  },
  // 写入线程信息；若线程未返回 messages，则保留当前消息避免 UI 被清空
  SET_THREAD_INFO (state, payload: SetThreadInfoPayload) {
    const targetSessionId = payload.sessionId ?? state.generativeCurrentSessionId
    if (!targetSessionId || !state.sessionStates[targetSessionId]) return

    const currentState = state.sessionStates[targetSessionId]
    const shouldUpdateMessages = Boolean(
      payload.threadInfo.messages && payload.threadInfo.messages.length > 0
    )
    const newMessages: any[] = shouldUpdateMessages
      ? payload.threadInfo.messages || []
      : currentState.messages || []

    state.sessionStates = {
      ...state.sessionStates,
      [targetSessionId]: {
        ...currentState,
        threadId: payload.threadInfo.threadId,
        threadMessages: payload.threadInfo.messages,
        threadInterrupt: payload.threadInfo.interrupt,
        messages: newMessages
      }
    }
  }
}

// Vuex 子模块导出
const robotModule: Module<RobotState, unknown> = {
  namespaced: true,
  state,
  mutations
}

export default robotModule
